import { useEffect, useRef, useState } from "react";
import { salonInfo } from "@/mocks/content";
import SpaButton from "@/components/base/SpaButton";

const TYPING_TEXT = "静寂の中に、本来の自分を取り戻す";

export default function HeroSection() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [typingDone, setTypingDone] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Panel open on mount
  useEffect(() => {
    const t1 = setTimeout(() => setPanelOpen(true), 100);
    const t2 = setTimeout(() => setImageReady(true), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Typing effect
  useEffect(() => {
    if (!imageReady) return;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setTypedText(TYPING_TEXT.slice(0, i));
      if (i >= TYPING_TEXT.length) {
        clearInterval(interval);
        setTypingDone(true);
      }
    }, 80);
    return () => clearInterval(interval);
  }, [imageReady]);

  const scrollDown = () => {
    const next = document.querySelector("#about");
    if (next) next.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative w-full h-screen min-h-[600px] overflow-hidden"
    >
      {/* Background Image — Cinematic Focus */}
      <div
        className="absolute inset-0 transition-all duration-1000"
        style={{
          filter: imageReady ? "grayscale(0%) blur(0px)" : "grayscale(80%) blur(8px)",
        }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=luxury%20mens%20spa%20interior%20with%20dim%20warm%20lighting%2C%20dark%20stone%20walls%2C%20minimalist%20japanese%20design%2C%20private%20treatment%20room%20with%20elegant%20wooden%20accents%2C%20serene%20atmosphere%2C%20deep%20charcoal%20and%20warm%20beige%20tones%2C%20sophisticated%20wellness%20space%2C%20no%20people&width=1920&height=1080&seq=hero1&orientation=landscape"
          alt="SEREN MEN'S SPA メインビジュアル"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
      </div>

      {/* Panel Overlay — Color Panel Reveal */}
      <div
        className="absolute inset-0 bg-[#8B7355] z-10 origin-left"
        style={{
          transform: panelOpen ? "scaleX(0)" : "scaleX(1)",
          transition: "transform 0.9s cubic-bezier(0.77, 0, 0.175, 1)",
        }}
      />

      {/* Asymmetry Grid Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          {/* Left: Vertical accent text */}
          <div className="hidden md:flex md:col-span-1 justify-center">
            <span
              className="text-[#FAFAF8]/40 text-xs tracking-[0.4em] font-light"
              style={{
                writingMode: "vertical-rl",
                fontFamily: "'Noto Serif JP', serif",
                opacity: imageReady ? 1 : 0,
                transition: "opacity 0.8s ease 1.2s",
              }}
            >
              MEN&apos;S SPA TOKYO
            </span>
          </div>

          {/* Center: Main Copy */}
          <div className="md:col-span-7 flex flex-col gap-6">
            <div
              style={{
                opacity: imageReady ? 1 : 0,
                transform: imageReady ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.6s ease 0.8s, transform 0.6s ease 0.8s",
              }}
            >
              <p
                className="text-[#8B7355] text-xs md:text-sm tracking-[0.3em] mb-4 font-light"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                SINCE 2018 — SHIBUYA, TOKYO
              </p>
              <h1
                className="text-[#FAFAF8] font-light leading-tight"
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontSize: "clamp(2rem, 5vw, 4rem)",
                  lineHeight: 1.4,
                }}
              >
                {typedText}
                {!typingDone && (
                  <span className="inline-block w-0.5 h-8 bg-[#8B7355] ml-1 animate-pulse align-middle" />
                )}
              </h1>
            </div>

            <div
              className="flex flex-col sm:flex-row gap-3 mt-2"
              style={{
                opacity: typingDone ? 1 : 0,
                transition: "opacity 0.6s ease 0.3s",
              }}
            >
              <SpaButton
                href="#services"
                variant="primary"
                className="w-full sm:w-auto justify-center"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <span>メニューを見る</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </SpaButton>

              <SpaButton
                href={`tel:${salonInfo.phone}`}
                variant="outline"
                className="w-full sm:w-auto justify-center"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                </svg>
                <span className="hidden sm:inline">{salonInfo.phone}</span>
                <span className="sm:hidden">電話する</span>
              </SpaButton>
            </div>
          </div>

          {/* Right: Floating accent card */}
          <div
            className="hidden md:block md:col-span-4"
            style={{
              opacity: imageReady ? 1 : 0,
              transform: imageReady ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.8s ease 1.4s, transform 0.8s ease 1.4s",
            }}
          >
            <div className="border border-[#FAFAF8]/20 p-6 backdrop-blur-sm bg-black/10">
              <p className="text-[#8B7355] text-xs tracking-[0.3em] mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                OPEN HOURS
              </p>
              {salonInfo.hours.map((h) => (
                <div key={h.day} className="flex justify-between items-center mb-2">
                  <span className="text-[#FAFAF8]/60 text-xs" style={{ fontFamily: "'Noto Serif JP', serif" }}>{h.day}</span>
                  <span className="text-[#FAFAF8] text-sm font-light" style={{ fontFamily: "'Noto Serif JP', serif" }}>{h.time}</span>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-[#FAFAF8]/10">
                <p className="text-[#FAFAF8]/40 text-xs" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  定休日: {salonInfo.closedDay}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollDown}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 cursor-pointer"
        style={{
          opacity: typingDone ? 1 : 0,
          transition: "opacity 0.6s ease 0.5s",
        }}
        aria-label="下にスクロール"
      >
        <span className="text-[#FAFAF8]/40 text-xs tracking-[0.3em]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
          SCROLL
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-[#FAFAF8]/40 to-transparent animate-pulse" />
      </button>
    </section>
  );
}
